import type { Project } from "@baton/api";
import { placeOf, type Place } from "../../accounts/location.ts";

/**
 * 020 FR-020-173, `spec.md` US5 scenario 7, `contracts/screens.md` § 020 Accounts and pipeline,
 * Projects by client: the headings the sidebar's Projects branch groups the visible projects under.
 * One heading per client, which is an account group holding the projects on its accounts or an
 * account that is in no group, the two kinds in one name order; then a heading naming no client for
 * the projects on an account the reader cannot see (`account_id` set beside a null `account`); then
 * Internal for the projects on no account, which is where the team's own work sits. Under a client
 * whose projects span more than one place each project carries its account's country, or the area
 * of an account located by a region alone (FR-020-262, 2026-09-28). While no
 * visible project is on an account there are no headings and the branch stays the flat list. A
 * project keeps the place the project list gave it inside its heading.
 */
export type ClientHeadingKind = "group" | "account" | "unnamed" | "internal";

export interface ClientProject {
  readonly project: Project;
  /** Where the project's account is, present only under a client that spans more than one place. */
  readonly place: Place | null;
}

export interface ClientHeading {
  /** Stable across reloads, so the collapsed state can be kept by it: the kind and the group's or account's id. */
  readonly id: string;
  readonly kind: ClientHeadingKind;
  /** The group's or the account's name; null for the two headings the catalogue names. */
  readonly name: string | null;
  readonly projects: readonly ClientProject[];
}

const UNNAMED_HEADING_ID = "unnamed";
const INTERNAL_HEADING_ID = "internal";

export function clientHeadings(projects: readonly Project[], locale: string): readonly ClientHeading[] {
  if (projects.every((project) => project.account_id === null)) return [];
  const clients = new Map<string, { readonly kind: "group" | "account"; readonly name: string; readonly projects: Project[] }>();
  const unnamed: Project[] = [];
  const internal: Project[] = [];
  for (const project of projects) {
    if (project.account_id === null) internal.push(project);
    else if (project.account === null) unnamed.push(project);
    else {
      const group = project.account.account_group;
      const id = group === null ? `account:${project.account.id}` : `group:${group.id}`;
      const client = clients.get(id) ?? { kind: group === null ? ("account" as const) : ("group" as const), name: group === null ? project.account.name : group.name, projects: [] };
      client.projects.push(project);
      clients.set(id, client);
    }
  }
  const collator = new Intl.Collator(locale, { sensitivity: "base", numeric: true });
  const named = [...clients.entries()]
    .sort(([aId, a], [bId, b]) => collator.compare(a.name, b.name) || aId.localeCompare(bId))
    .map<ClientHeading>(([id, client]) => {
      const places = client.projects.map((project) => (project.account === null ? null : placeOf(project.account)));
      const spans = new Set(places.map((place) => (place === null ? "" : place.kind === "country" ? place.code : `area:${place.area.code}`))).size > 1;
      return { id, kind: client.kind, name: client.name, projects: client.projects.map((project, index) => ({ project, place: spans ? (places[index] ?? null) : null })) };
    });
  const plain = (rows: readonly Project[]): readonly ClientProject[] => rows.map((project) => ({ project, place: null }));
  return [...named, ...(unnamed.length === 0 ? [] : [{ id: UNNAMED_HEADING_ID, kind: "unnamed" as const, name: null, projects: plain(unnamed) }]), ...(internal.length === 0 ? [] : [{ id: INTERNAL_HEADING_ID, kind: "internal" as const, name: null, projects: plain(internal) }])];
}
